import { Spin } from 'antd'
import CantileverButton from 'components/CantileverButton'
import { compareDesc } from 'date-fns'
import {
  InspectionListProps,
  JobCard,
  useInspectionsPageData,
  WebGLLayer,
} from 'features/assetDatabase'
import React, { useState } from 'react'
import styled from 'styled-components'

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-bottom: 1rem;
`

const NoInspectionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 96px;
  align-self: stretch;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const StyledSpinContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

export const JobCards: React.FC<{
  showAddButtons?: boolean
  showJobHandoffButtons?: boolean
  linkToAsset?: boolean
  siteID?: string
  unitID?: string
  jobID?: string
}> = ({
  linkToAsset,
  siteID,
  unitID,
  jobID,
  showAddButtons = true,
  showJobHandoffButtons = true,
}) => {
  const [numberOfJobsToShow, setNumberOfJobsToShow] = useState(5)

  if (!siteID && !unitID && !jobID) {
    throw new Error(
      'Unable to load JobCards. No ID provided (Site, Unit, Job)!'
    )
  }

  const {
    site,
    jobs,
    units,
    inspections,
    components,
    assetModels,
    componentDataFrames,
    componentDatasets,
    isLoading,
    refreshInspectionData,
  } = useInspectionsPageData({ siteID, unitID })

  /**
   * This creates a Map of the job id => JobListProps.
   * The Job List props are shown in the type above and represent the 1:N job to associated items relationship
   */
  const inspectionListProps = React.useMemo(() => {
    const result = new Map<string, InspectionListProps>()
    if (site === undefined) {
      return undefined
    }
    jobs
      .filter((job) => (jobID ? job.id === jobID : true))
      .map((job) => {
        const jobInspections = inspections.filter(
          (inspection) =>
            inspection.tags.job === job.id &&
            (unitID ? inspection.tags.unit === unitID : true) // If we have a unitID, filter to only those inspections
        )

        const jobComponents = components.filter((component) =>
          jobInspections
            .map((inspection) => inspection.tags.component)
            .includes(component.id)
        )

        const jobUnits = units.filter((unit) =>
          jobInspections
            .map((inspection) => inspection.tags.unit)
            .includes(unit.id)
        )

        if (unitID && jobInspections.length === 0) {
          // Skip adding the result if we're filtering to a unit, and this unit has no inspections
        } else {
          result.set(job.id, {
            job: job,
            inspections: jobInspections,
            components: new Map(
              jobComponents.map((component) => [component.id, component])
            ),
            assetModels,
            units: new Map(jobUnits.map((unit) => [unit.id, unit])),
            cdfs: componentDataFrames,
            cdses: componentDatasets,
            siteID: site.id,
          })
        }
      })

    //ensure that final list of jobs is sorted by date
    return Array.from(result.values()).sort((a, b) =>
      compareDesc(
        new Date(a.job.data.start_date),
        new Date(b.job.data.start_date)
      )
    )
  }, [
    inspections,
    jobs,
    components,
    units,
    assetModels,
    componentDataFrames,
    componentDatasets,
    site,
    unitID,
    jobID,
  ])

  return isLoading ? (
    <StyledSpinContainer>
      <Spin spinning={isLoading} />
    </StyledSpinContainer>
  ) : (
    <WebGLLayer>
      <CardList>
        {inspectionListProps?.length === 0 && (
          <NoInspectionsContainer>No Inspections</NoInspectionsContainer>
        )}
        {inspectionListProps
          ?.slice(0, numberOfJobsToShow)
          .map((ilp, i) => (
            <JobCard
              key={i}
              ilp={ilp}
              linkToAsset={linkToAsset}
              forceRefreshInspections={refreshInspectionData}
              showAddInspection={showAddButtons}
              showJobHandoff={showJobHandoffButtons}
            />
          ))}
        {numberOfJobsToShow < (inspectionListProps?.length || 0) && (
          <CantileverButton
            onClick={() => setNumberOfJobsToShow((prev) => prev + 5)}>
            Load More Jobs
          </CantileverButton>
        )}
      </CardList>
    </WebGLLayer>
  )
}
